// frontend/src/components/Dashboard/ShipmentDetailsModal.jsx - UPDATED
import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faTimes,
  faTruck,
  faMapMarkerAlt,
  faCalendar,
  faBox,
  faEdit,
  faCheckCircle,
  faClock,
  faShip,
  faPlane,
  faRoad,
  faUser,
  faEnvelope,
  faPhone,
  faBoxes,
  faWeightScale,
  faAnchor,
  faClipboard,
  faInfoCircle,
  faBuilding,
  faGlobe,
  faWarehouse,
  faCity,
  faSpinner,
  faSave,
  faUndo,
  faEye,
  faLocationDot,
  faCheckDouble,
  faRocket,
  faExclamationTriangle,
  faAward,
  faHandshake,
  faShieldAlt,
  faLeaf,
  faCow,
  faTractor,
  faStore,
} from "@fortawesome/free-solid-svg-icons";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";

// ============================================
// CONSTANTS - Globally Sourced (Confidential)
// ============================================
const SOURCE_LABEL = "Globally Sourced";
const SOURCE_ICON = faGlobe;

// ============================================
// HELPERS
// ============================================
const getStatusColor = (status) => {
  const colors = {
    ordered: "#D9A321",
    processing: "#3b82f6",
    packed: "#8b5cf6",
    shipped: "#8b5cf6",
    in_transit: "#f59e0b",
    customs: "#ef4444",
    local_delivery: "#10b981",
    out_for_delivery: "#f59e0b",
    delivered: "#16a34a",
    delayed: "#dc2626",
    cancelled: "#6b7280",
  };
  return colors[status] || "#5A5A5A";
};

const getStatusIcon = (status) => {
  switch (status) {
    case "delivered":
      return faCheckDouble;
    case "cancelled":
      return faTimes;
    case "delayed":
      return faExclamationTriangle;
    case "shipped":
      return faRocket;
    case "in_transit":
      return faTruck;
    case "customs":
      return faShip;
    default:
      return faClock;
  }
};

const getStatusLabel = (status) => {
  const labels = {
    ordered: "Order Received",
    processing: "Processing",
    packed: "Packed",
    shipped: "Shipped",
    in_transit: "In Transit",
    customs: "Customs Clearance",
    local_delivery: "Local Delivery",
    out_for_delivery: "Out for Delivery",
    delivered: "Delivered",
    delayed: "Delayed",
    cancelled: "Cancelled",
  };
  return labels[status] || status?.replace(/_/g, " ") || "N/A";
};

const getShippingMethodIcon = (method) => {
  switch (method) {
    case "air":
      return faPlane;
    case "sea":
      return faShip;
    case "road":
      return faRoad;
    case "courier":
      return faBox;
    default:
      return faTruck;
  }
};

const getShippingMethodLabel = (method) => {
  const labels = {
    air: "Air Freight",
    sea: "Sea Freight",
    road: "Road Transport",
    courier: "Courier",
    combined: "Combined",
  };
  return labels[method] || method || "N/A";
};

const formatDate = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatDateShort = (date) => {
  if (!date) return "N/A";
  return new Date(date).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// ============================================
// MAIN COMPONENT
// ============================================
const ShipmentDetailsModal = ({ shipment, onClose, onUpdate, isAdmin }) => {
  const [expandedSections, setExpandedSections] = useState({
    customer: true,
    route: true,
    details: true,
    bulk: false,
    products: true,
    timeline: true,
    customs: false,
    address: true,
    dates: true,
    notes: true,
    supplier: true,
  });

  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [localUpdateData, setLocalUpdateData] = useState({
    status: shipment?.status || "",
    location: shipment?.currentLocation || "",
    description: "",
  });

  if (!shipment) return null;

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleSubmitUpdate = async (e) => {
    e.preventDefault();
    if (!localUpdateData.status) {
      alert("Please select a status");
      return;
    }
    setLoading(true);
    try {
      await onUpdate(
        shipment._id,
        localUpdateData.status,
        localUpdateData.location,
        localUpdateData.description,
      );
      setEditing(false);
      setLocalUpdateData({
        status: localUpdateData.status,
        location: localUpdateData.location,
        description: "",
      });
    } catch (error) {
      console.error("Error updating shipment:", error);
      alert(error.response?.data?.message || "Failed to update shipment");
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEdit = () => {
    setEditing(false);
    setLocalUpdateData({
      status: shipment.status || "",
      location: shipment.currentLocation || "",
      description: "",
    });
  };

  const getProgressPercentage = (status) => {
    const steps = {
      ordered: 5,
      processing: 20,
      packed: 35,
      shipped: 50,
      in_transit: 65,
      customs: 75,
      local_delivery: 85,
      out_for_delivery: 95,
      delivered: 100,
      delayed: 50,
      cancelled: 0,
    };
    return steps[status] || 0;
  };

  const isDelayed = shipment.isDelayed?.() || false;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal-content shipment-details-modal large"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Modal Header */}
        <div className="modal-header">
          <h2>
            <FontAwesomeIcon icon={faTruck} /> Shipment Details
            <span className="origin-badge">
              <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
            </span>
            <span className="supplier-badge">
              <FontAwesomeIcon icon={faAward} /> Premium Quality
            </span>
          </h2>
          <button className="close-btn" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="modal-body">
          {/* Tracking Header */}
          <div className="tracking-header">
            <div className="tracking-number">
              <span className="tracking-label">Tracking Number:</span>
              <span className="tracking-code">{shipment.trackingNumber}</span>
              <span className="global-tag">
                <FontAwesomeIcon icon={faGlobe} /> Global Supply Chain
              </span>
            </div>
            <div className="tracking-status-badges">
              {shipment.orderType === "bulk_farmer" && (
                <span className="badge bulk">Bulk Shipment</span>
              )}
              {shipment.orderType === "inventory" && (
                <span className="badge inventory">Inventory</span>
              )}
              {isDelayed && <span className="badge delayed">Delayed</span>}
              {shipment.stockUpdated && (
                <span className="badge stock-updated">Stock Updated</span>
              )}
              <span className="badge premium">
                <FontAwesomeIcon icon={faAward} /> Premium Quality
              </span>
              <span className="badge global">
                <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
              </span>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="progress-section">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${getProgressPercentage(shipment.status)}%` }}
              />
            </div>
            <div className="progress-labels">
              <span>Ordered</span>
              <span>Processing</span>
              <span>Shipped</span>
              <span>In Transit</span>
              <span>Customs</span>
              <span>Delivered</span>
            </div>
          </div>

          {/* Status Section */}
          <div
            className="status-section"
            style={{ borderLeftColor: getStatusColor(shipment.status) }}
          >
            <div className="status-header">
              <div className="status-display">
                <FontAwesomeIcon
                  icon={getStatusIcon(shipment.status)}
                  style={{ color: getStatusColor(shipment.status) }}
                />
                <span
                  className="status-label"
                  style={{ color: getStatusColor(shipment.status) }}
                >
                  {getStatusLabel(shipment.status)}
                </span>
                {shipment.currentLocation && (
                  <span className="current-location">
                    <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                    {shipment.currentLocation}
                  </span>
                )}
                {shipment.estimatedDelivery && (
                  <span className="estimated-delivery">
                    <FontAwesomeIcon icon={faCalendar} /> Est:{" "}
                    {formatDateShort(shipment.estimatedDelivery)}
                  </span>
                )}
                {shipment.bulkDetails?.vesselName && (
                  <span className="vessel-name">
                    <FontAwesomeIcon icon={faShip} />{" "}
                    {shipment.bulkDetails.vesselName}
                  </span>
                )}
              </div>
              {isAdmin && (
                <div className="status-actions">
                  {!editing ? (
                    <button
                      className="btn-edit"
                      onClick={() => setEditing(true)}
                    >
                      <FontAwesomeIcon icon={faEdit} /> Update Status
                    </button>
                  ) : (
                    <button className="btn-cancel" onClick={handleCancelEdit}>
                      <FontAwesomeIcon icon={faUndo} /> Cancel
                    </button>
                  )}
                </div>
              )}
            </div>

            {isAdmin && editing && (
              <form
                onSubmit={handleSubmitUpdate}
                className="status-update-form"
              >
                <div className="form-row">
                  <div className="form-group">
                    <label>Status *</label>
                    <select
                      value={localUpdateData.status}
                      onChange={(e) =>
                        setLocalUpdateData({
                          ...localUpdateData,
                          status: e.target.value,
                        })
                      }
                      className="status-select"
                      required
                    >
                      <option value="ordered">Order Received</option>
                      <option value="processing">Processing</option>
                      <option value="packed">Packed</option>
                      <option value="shipped">Shipped</option>
                      <option value="in_transit">In Transit</option>
                      <option value="customs">Customs Clearance</option>
                      <option value="local_delivery">Local Delivery</option>
                      <option value="out_for_delivery">Out for Delivery</option>
                      <option value="delivered">Delivered</option>
                      <option value="delayed">Delayed</option>
                      <option value="cancelled">Cancelled</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Location</label>
                    <input
                      type="text"
                      placeholder="Current location"
                      value={localUpdateData.location}
                      onChange={(e) =>
                        setLocalUpdateData({
                          ...localUpdateData,
                          location: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Update Description</label>
                  <input
                    type="text"
                    placeholder="Brief update description"
                    value={localUpdateData.description}
                    onChange={(e) =>
                      setLocalUpdateData({
                        ...localUpdateData,
                        description: e.target.value,
                      })
                    }
                  />
                </div>
                <div className="form-actions">
                  <button
                    type="submit"
                    className="btn-primary"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin /> Updating...
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faSave} /> Update Status
                      </>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>

          {/* Supplier Information - Globally Sourced */}
          <div className="detail-section supplier">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("supplier")}
            >
              <h3>
                <FontAwesomeIcon icon={faBuilding} /> Supply Chain Information
                <span className="supplier-badge">
                  <FontAwesomeIcon icon={faGlobe} /> Global Network
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.supplier ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.supplier && (
              <div className="supplier-details">
                <div className="supplier-info-card">
                  <FontAwesomeIcon icon={faGlobe} className="supplier-icon" />
                  <div className="supplier-info">
                    <strong>Globally Sourced Supply Chain</strong>
                    <span>
                      <FontAwesomeIcon icon={faGlobe} /> Worldwide Sourcing &bull;
                      <FontAwesomeIcon icon={faAward} /> Premium Quality
                    </span>
                  </div>
                </div>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Origin:</span>
                    <span className="detail-value">
                      <span className="origin-tag">
                        <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                      </span>
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Quality Standard:</span>
                    <span className="detail-value">
                      <FontAwesomeIcon icon={faAward} /> Premium Grade
                    </span>
                  </div>
                  {shipment.origin?.warehouse && (
                    <div className="detail-item">
                      <span className="detail-label">
                        <FontAwesomeIcon icon={faWarehouse} /> Warehouse:
                      </span>
                      <span className="detail-value">
                        {shipment.origin.warehouse}
                      </span>
                    </div>
                  )}
                  <div className="detail-item">
                    <span className="detail-label">Sourcing Type:</span>
                    <span className="detail-value">
                      <span className="global-tag">
                        <FontAwesomeIcon icon={faGlobe} /> International
                      </span>
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Customer Information */}
          {shipment.customer?.name && (
            <div className="detail-section">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("customer")}
              >
                <h3>
                  <FontAwesomeIcon icon={faUser} /> Customer Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.customer ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.customer && (
                <div className="customer-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Name:</span>
                      <span className="detail-value">
                        {shipment.customer.name}
                      </span>
                    </div>
                    {shipment.customer.email && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faEnvelope} /> Email:
                        </span>
                        <span className="detail-value">
                          {shipment.customer.email}
                        </span>
                      </div>
                    )}
                    {shipment.customer.phone && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faPhone} /> Phone:
                        </span>
                        <span className="detail-value">
                          <a
                            href={`tel:${shipment.customer.phone}`}
                            className="phone-link"
                          >
                            {shipment.customer.phone}
                          </a>
                        </span>
                      </div>
                    )}
                    {shipment.customer.farmName && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faTractor} /> Farm:
                        </span>
                        <span className="detail-value">
                          {shipment.customer.farmName}
                        </span>
                      </div>
                    )}
                    {shipment.customer.whatsapp && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faWhatsapp} /> WhatsApp:
                        </span>
                        <span className="detail-value">
                          <a
                            href={`https://wa.me/${shipment.customer.whatsapp}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="whatsapp-link"
                          >
                            <FontAwesomeIcon icon={faWhatsapp} />{" "}
                            {shipment.customer.whatsapp}
                          </a>
                        </span>
                      </div>
                    )}
                    {shipment.customer.userType && (
                      <div className="detail-item">
                        <span className="detail-label">Customer Type:</span>
                        <span className="detail-value">
                          <span className={`user-type-badge ${shipment.customer.userType}`}>
                            <FontAwesomeIcon
                              icon={shipment.customer.userType === "farmer" ? faTractor : faStore}
                            />{" "}
                            {shipment.customer.userType === "farmer" ? "Farmer" : "Customer"}
                          </span>
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Route Information - Globally Sourced */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("route")}
            >
              <h3>
                <FontAwesomeIcon icon={faMapMarkerAlt} /> Route Information
                <span className="route-badge">
                  <FontAwesomeIcon icon={faGlobe} /> Global → Sierra Leone
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.route ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.route && (
              <div className="route-info">
                <div className="route-points">
                  <div className="route-point origin">
                    <div className="route-label">
                      <FontAwesomeIcon icon={faGlobe} /> Origin
                    </div>
                    <div className="route-value">
                      {shipment.origin?.city || "Global Supply Point"}
                    </div>
                    <div className="route-sub">
                      <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                    </div>
                    {shipment.bulkDetails?.portOfLoading && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faAnchor} /> Port:{" "}
                        {shipment.bulkDetails.portOfLoading}
                      </div>
                    )}
                  </div>
                  <div className="route-arrow">→</div>
                  <div className="route-point destination">
                    <div className="route-label">
                      <FontAwesomeIcon icon={faMapMarkerAlt} /> Destination
                    </div>
                    <div className="route-value">
                      {shipment.destination?.city || "Freetown"}
                    </div>
                    <div className="route-sub">
                      {shipment.destination?.country || "Sierra Leone"} 🇸🇱
                    </div>
                    {shipment.bulkDetails?.portOfDischarge && (
                      <div className="route-sub">
                        <FontAwesomeIcon icon={faAnchor} /> Port:{" "}
                        {shipment.bulkDetails.portOfDischarge}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Shipment Details */}
          <div className="detail-section">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("details")}
            >
              <h3>
                <FontAwesomeIcon icon={faBox} /> Shipment Details
              </h3>
              <span className="toggle-icon">
                {expandedSections.details ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.details && (
              <div className="details-grid">
                <div className="detail-item">
                  <span className="detail-label">Shipping Method:</span>
                  <span className="detail-value">
                    <FontAwesomeIcon
                      icon={getShippingMethodIcon(shipment.shippingMethod)}
                    />{" "}
                    {getShippingMethodLabel(shipment.shippingMethod)}
                  </span>
                </div>
                {shipment.totalWeight > 0 && (
                  <div className="detail-item">
                    <span className="detail-label">
                      <FontAwesomeIcon icon={faWeightScale} /> Total Weight:
                    </span>
                    <span className="detail-value">
                      {shipment.totalWeight} kg
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Days in Transit:</span>
                  <span className="detail-value">
                    {shipment.daysInTransit || 0} days
                  </span>
                </div>
                {shipment.trackingUrl && (
                  <div className="detail-item">
                    <span className="detail-label">Tracking URL:</span>
                    <span className="detail-value">
                      <a
                        href={shipment.trackingUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FontAwesomeIcon icon={faEye} /> View Tracking
                      </a>
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Order Type:</span>
                  <span className="detail-value">
                    <span className={`order-type-badge ${shipment.orderType || "retail"}`}>
                      {shipment.orderType === "bulk_farmer" ? "Bulk" : 
                       shipment.orderType === "inventory" ? "Inventory" : "Retail"}
                    </span>
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Bulk Details */}
          {shipment.bulkDetails?.isBulkShipment && (
            <div className="detail-section bulk">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("bulk")}
              >
                <h3>
                  <FontAwesomeIcon icon={faShip} /> Bulk Shipment Details
                  <span className="bulk-badge">
                    <FontAwesomeIcon icon={faAnchor} /> Global Logistics
                  </span>
                </h3>
                <span className="toggle-icon">
                  {expandedSections.bulk ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.bulk && (
                <div className="bulk-details">
                  <div className="detail-grid">
                    {shipment.bulkDetails.containerNumber && (
                      <div className="detail-item">
                        <span className="detail-label">Container:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.containerNumber}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.containerType && (
                      <div className="detail-item">
                        <span className="detail-label">Container Type:</span>
                        <span className="detail-value">
                          {shipment.bulkDetails.containerType}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.vesselName && (
                      <div className="detail-item">
                        <span className="detail-label">Vessel:</span>
                        <span className="detail-value">
                          <FontAwesomeIcon icon={faShip} />{" "}
                          {shipment.bulkDetails.vesselName}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.portOfLoading && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Loading:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.portOfLoading}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.portOfDischarge && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faAnchor} /> Port of Discharge:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.portOfDischarge}
                        </span>
                      </div>
                    )}
                    {shipment.bulkDetails.palletsCount > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">
                          <FontAwesomeIcon icon={faBoxes} /> Pallets:
                        </span>
                        <span className="detail-value">
                          {shipment.bulkDetails.palletsCount}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Products */}
          {shipment.products?.length > 0 && (
            <div className="detail-section products">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("products")}
              >
                <h3>
                  <FontAwesomeIcon icon={faBox} /> Products in Shipment
                  <span className="product-count">
                    ({shipment.products.length} items)
                  </span>
                  <span className="origin-summary">
                    <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                  </span>
                </h3>
                <span className="toggle-icon">
                  {expandedSections.products ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.products && (
                <div className="table-responsive">
                  <table className="items-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Quantity</th>
                        <th>Unit</th>
                        <th>Price</th>
                        <th>Origin</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {shipment.products.map((product, index) => (
                        <tr key={index}>
                          <td>
                            {product.name || product.productId?.name || "N/A"}
                          </td>
                          <td>{product.quantity}</td>
                          <td>{product.unit || "kg"}</td>
                          <td>${(product.price || 0).toFixed(2)}</td>
                          <td>
                            <span className="origin-tag">
                              <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
                            </span>
                          </td>
                          <td>
                            $
                            {((product.price || 0) * product.quantity).toFixed(
                              2,
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan="4"></td>
                        <td>
                          <strong>Total:</strong>
                        </td>
                        <td>
                          <strong>
                            $
                            {shipment.products
                              .reduce(
                                (sum, p) => sum + (p.price || 0) * p.quantity,
                                0,
                              )
                              .toFixed(2)}
                          </strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* Timeline */}
          <div className="detail-section timeline">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("timeline")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Tracking Timeline
                <span className="timeline-count">
                  ({shipment.timeline?.length || 1} events)
                </span>
              </h3>
              <span className="toggle-icon">
                {expandedSections.timeline ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.timeline && (
              <div className="timeline">
                {shipment.timeline?.length > 0 ? (
                  shipment.timeline.map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div
                        className="timeline-dot"
                        style={{ background: getStatusColor(event.status) }}
                      />
                      <div className="timeline-content">
                        <div className="timeline-status">
                          {getStatusLabel(event.status)}
                          {event.updatedBy && event.updatedBy !== "system" && (
                            <span className="updated-by">
                              by {event.updatedBy}
                            </span>
                          )}
                        </div>
                        {event.location && (
                          <div className="timeline-location">
                            <FontAwesomeIcon icon={faMapMarkerAlt} />{" "}
                            {event.location}
                          </div>
                        )}
                        {event.description && (
                          <div className="timeline-description">
                            {event.description}
                          </div>
                        )}
                        <div className="timeline-date">
                          {formatDate(event.timestamp)}
                        </div>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="timeline-item">
                    <div
                      className="timeline-dot"
                      style={{ background: "#D9A321" }}
                    />
                    <div className="timeline-content">
                      <div className="timeline-status">Shipment Created</div>
                      <div className="timeline-date">
                        {formatDate(shipment.createdAt)}
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Customs Information */}
          {shipment.customs && (
            <div className="detail-section customs">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("customs")}
              >
                <h3>
                  <FontAwesomeIcon icon={faClipboard} /> Customs Information
                </h3>
                <span className="toggle-icon">
                  {expandedSections.customs ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.customs && (
                <div className="customs-details">
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Cleared:</span>
                      <span
                        className={
                          shipment.customs.cleared
                            ? "status-active"
                            : "status-inactive"
                        }
                      >
                        {shipment.customs.cleared ? "Cleared" : "Pending"}
                      </span>
                    </div>
                    {shipment.customs.cleared &&
                      shipment.customs.clearanceDate && (
                        <div className="detail-item">
                          <span className="detail-label">Clearance Date:</span>
                          <span className="detail-value">
                            {formatDate(shipment.customs.clearanceDate)}
                          </span>
                        </div>
                      )}
                    {shipment.customs.dutyAmount > 0 && (
                      <div className="detail-item">
                        <span className="detail-label">Duty Amount:</span>
                        <span className="detail-value">
                          ${shipment.customs.dutyAmount.toFixed(2)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Dates */}
          <div className="detail-section dates">
            <div
              className="section-header-clickable"
              onClick={() => toggleSection("dates")}
            >
              <h3>
                <FontAwesomeIcon icon={faCalendar} /> Important Dates
              </h3>
              <span className="toggle-icon">
                {expandedSections.dates ? "▲" : "▼"}
              </span>
            </div>
            {expandedSections.dates && (
              <div className="dates-grid">
                <div className="detail-item">
                  <span className="detail-label">Created:</span>
                  <span className="detail-value">
                    {formatDate(shipment.createdAt)}
                  </span>
                </div>
                {shipment.shippedAt && (
                  <div className="detail-item">
                    <span className="detail-label">Shipped:</span>
                    <span className="detail-value">
                      {formatDate(shipment.shippedAt)}
                    </span>
                  </div>
                )}
                {shipment.estimatedDelivery && (
                  <div className="detail-item">
                    <span className="detail-label">Est. Delivery:</span>
                    <span className="detail-value">
                      {formatDate(shipment.estimatedDelivery)}
                      {isDelayed && (
                        <span className="delayed-warning"> (Delayed)</span>
                      )}
                    </span>
                  </div>
                )}
                {shipment.deliveredAt && (
                  <div className="detail-item">
                    <span className="detail-label">Delivered:</span>
                    <span className="detail-value">
                      {formatDate(shipment.deliveredAt)}
                    </span>
                  </div>
                )}
                <div className="detail-item">
                  <span className="detail-label">Last Updated:</span>
                  <span className="detail-value">
                    {formatDate(shipment.lastUpdated)}
                  </span>
                </div>
              </div>
            )}
          </div>

          {/* Notes */}
          {(shipment.notes || shipment.internalNotes) && (
            <div className="detail-section notes">
              <div
                className="section-header-clickable"
                onClick={() => toggleSection("notes")}
              >
                <h3>
                  <FontAwesomeIcon icon={faInfoCircle} /> Notes
                </h3>
                <span className="toggle-icon">
                  {expandedSections.notes ? "▲" : "▼"}
                </span>
              </div>
              {expandedSections.notes && (
                <div className="notes-details">
                  {shipment.notes && (
                    <div className="note-item public">
                      <span className="note-label">Public:</span>
                      <p>{shipment.notes}</p>
                    </div>
                  )}
                  {shipment.internalNotes && (
                    <div className="note-item internal">
                      <span className="note-label">Internal:</span>
                      <p>{shipment.internalNotes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
          {shipment.customer?.whatsapp && (
            <a
              href={`https://wa.me/${shipment.customer.whatsapp}?text=Hi%20${encodeURIComponent(shipment.customer.name || "")}%2C%20your%20shipment%20${encodeURIComponent(shipment.trackingNumber || "")}%20is%20currently%20${encodeURIComponent(getStatusLabel(shipment.status))}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-whatsapp"
            >
              <FontAwesomeIcon icon={faWhatsapp} /> Contact Customer
            </a>
          )}
          <span className="global-footer-badge">
            <FontAwesomeIcon icon={faGlobe} /> Globally Sourced
          </span>
        </div>
      </div>
    </div>
  );
};

export default ShipmentDetailsModal;